import { parseDateValue, useDateFormat, useFormatDate } from '@ben/common/date';
import DateInput, { type DateInputValue } from '@ben/orion/date-input';
import type { WidgetProps } from '@rjsf/utils';
import { format } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const DatePickerWidget = ({
  id,
  label,
  value,
  disabled,
  required,
  onChange,
  rawErrors,
  hideError,
  schema,
  uiSchema,
  registry,
  name,
}: WidgetProps) => {
  const formContext = registry?.formContext;
  const { inputFormat } = useDateFormat();

  const onDateChange = useCallback(
    (date: DateInputValue) => {
      onChange(date ? format(date, 'yyyy-MM-dd') : undefined);
    },
    [onChange]
  );

  const isPastDateField = useMemo(() => {
    const fieldName = name || id;
    return fieldName.toLowerCase().includes('pastdate');
  }, [name, id]);

  const maxDate = useMemo(() => {
    if (uiSchema?.maxDate) {
      return parseDateValue(uiSchema.maxDate);
    }
    if (isPastDateField) {
      return new Date();
    }
    return undefined;
  }, [uiSchema?.maxDate, isPastDateField]);

  const hintText = useHintText(uiSchema, isPastDateField);

  return (
    <DateInput
      id={id}
      label={label}
      value={value ? parseDateValue(value) : null}
      onChange={onDateChange}
      errorMessage={
        rawErrors && rawErrors.length > 0 && !hideError
          ? rawErrors?.join(', ')
          : undefined
      }
      disabled={disabled || schema.readOnly}
      isOptional={!required && !formContext?.hideOptionalLabels}
      width="100%"
      clearable={uiSchema?.clearable || formContext?.clearableFields}
      minDate={uiSchema?.minDate ? parseDateValue(uiSchema.minDate) : undefined}
      maxDate={maxDate}
      hintText={hintText ?? schema?.description}
      dateFormat={formContext?.dateFormat ?? inputFormat}
    />
  );
};

export default DatePickerWidget;

const useHintText = (
  uiSchema: WidgetProps['uiSchema'],
  isPastDateField: boolean
) => {
  const { t } = useTranslation();
  const formatDate = useFormatDate();

  if (uiSchema?.minDate && uiSchema.maxDate) {
    return t('schemaForm.datePickerWidget.hintText.minMax', {
      minDate: formatDate(uiSchema.minDate),
      maxDate: formatDate(uiSchema.maxDate),
    });
  }

  if (uiSchema?.minDate) {
    return t('schemaForm.datePickerWidget.hintText.min', {
      minDate: formatDate(uiSchema.minDate),
    });
  }

  if (uiSchema?.maxDate) {
    return t('schemaForm.datePickerWidget.hintText.max', {
      maxDate: formatDate(uiSchema.maxDate),
    });
  }

  if (isPastDateField) {
    return t('schemaForm.datePickerWidget.hintText.max', {
      maxDate: formatDate(new Date()),
    });
  }

  return;
};
